/**
 * <facets-pills> — the horizontal pill bar + filter drawer used by the
 * `pills` filter layout of main-collection-product-grid.
 *
 * Deliberately does NOT reuse Dawn's <facet-filters-form>: that element submits
 * on every `input` event, and this layout applies filters only when the drawer's
 * "Apply filters" button is pressed. It still hands the final params to
 * FacetFiltersForm.renderPage() (facets.js) so the fetch/cache/history/section
 * rendering pipeline stays shared.
 *
 * Sorting and removing an active-filter chip apply immediately — they act on the
 * already-applied URL, so unapplied drawer edits never leak into them.
 */
class FacetsPills extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.drawer = this.querySelector('[data-drawer]');
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.form = this.querySelector('#FacetFiltersPillsForm');

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onFacetsRendered = this.onFacetsRendered.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('facets:rendered', this.onFacetsRendered);

    this.syncAllPriceSliders();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('facets:rendered', this.onFacetsRendered);
    this.unlockScroll();
  }

  onDocumentClick(event) {
    const disclosure = this.querySelector('[data-sort-disclosure][open]');
    if (disclosure && !disclosure.contains(event.target)) disclosure.open = false;
  }

  /* ---------------------------------------------------------------- events */

  onClick(event) {
    const openTrigger = event.target.closest('[data-open-drawer]');
    if (openTrigger) {
      event.preventDefault();
      this.opener = openTrigger;
      this.openDrawer(openTrigger.dataset.filterTarget);
      return;
    }

    if (event.target.closest('[data-close-drawer]') || event.target === this.overlay) {
      event.preventDefault();
      this.closeDrawer();
      return;
    }

    const sortOption = event.target.closest('[data-sort-value]');
    if (sortOption) {
      event.preventDefault();
      this.applySort(sortOption.dataset.sortValue);
      return;
    }

    const chip = event.target.closest('[data-facet-remove]');
    if (chip) {
      event.preventDefault();
      this.applyUrl(chip.getAttribute('href'));
      return;
    }

    const showMore = event.target.closest('[data-show-more]');
    if (showMore) {
      event.preventDefault();
      this.toggleShowMore(showMore);
      return;
    }

    if (event.target.closest('[data-clear-filters]')) {
      event.preventDefault();
      this.clearFilters();
      return;
    }

    if (event.target.closest('[data-apply-filters]')) {
      event.preventDefault();
      this.applyFilters();
    }
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;

    const openSort = this.querySelector('[data-sort-disclosure][open]');
    if (openSort) {
      openSort.open = false;
      openSort.querySelector('summary')?.focus();
      return;
    }

    if (this.isDrawerOpen) this.closeDrawer();
  }

  onInput(event) {
    const search = event.target.closest('[data-filter-search]');
    if (search) {
      this.filterValues(search);
      return;
    }

    const slider = event.target.closest('[data-slider-min], [data-slider-max]');
    if (slider) {
      this.onSliderInput(slider);
      return;
    }

    const priceInput = event.target.closest('[data-price-min], [data-price-max]');
    if (priceInput) this.syncSliderFromInputs(priceInput.closest('price-range'));
  }

  /**
   * facets.js dispatches this after it has swapped the freshly fetched markup in.
   * The pill row and the sort label live outside every container Dawn rewrites, so
   * they are refreshed here from the same parsed document.
   */
  onFacetsRendered(event) {
    const parsed = event.detail?.html;
    if (!parsed) return;

    ['.facets-pills__pills', '.facets-pills__sort-row'].forEach((selector) => {
      const source = parsed.querySelector(selector);
      const target = this.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });

    const chips = this.querySelector('.facets-pills__chips');
    if (chips) chips.classList.toggle('facets-pills__chips--empty', chips.children.length === 0);

    this.syncAllPriceSliders();
  }

  /* ---------------------------------------------------------------- drawer */

  get isDrawerOpen() {
    return this.drawer && !this.drawer.hidden;
  }

  openDrawer(targetGroupId) {
    if (!this.drawer) return;

    // Snapshot the applied state so closing without applying rolls edits back.
    this.snapshot = this.serializeForm();

    this.querySelectorAll('.facets-pills__group').forEach((group) => {
      group.open = Boolean(targetGroupId) && group.id === targetGroupId;
    });

    this.drawer.hidden = false;
    if (this.overlay) this.overlay.hidden = false;
    this.lockScroll();

    // Two frames: `hidden` removal has to be painted before the transition class
    // lands, otherwise the drawer jumps into place instead of sliding.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        if (this.overlay) this.overlay.classList.add('is-open');
      });
    });

    this.syncAllPriceSliders();

    const focusTarget = targetGroupId
      ? this.querySelector(`#${CSS.escape(targetGroupId)} summary`)
      : this.querySelector('[data-close-drawer]');
    focusTarget?.focus({ preventScroll: true });
  }

  closeDrawer({ keepState = false } = {}) {
    if (!this.drawer || this.drawer.hidden) return;

    if (!keepState && this.snapshot) this.restoreForm(this.snapshot);

    this.drawer.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-open');
    this.unlockScroll();

    const finish = () => {
      this.drawer.hidden = true;
      if (this.overlay) this.overlay.hidden = true;
    };

    if (window.Theme?.config?.motionReduced) {
      finish();
    } else {
      this.drawer.addEventListener('transitionend', finish, { once: true });
      // Guard against a missed transitionend (drawer hidden mid-animation).
      setTimeout(() => {
        if (!this.drawer.classList.contains('is-open')) finish();
      }, 400);
    }

    // The pill row is re-rendered after a filter change, so fall back by index.
    const opener = this.opener?.isConnected ? this.opener : this.querySelector('[data-open-drawer]');
    opener?.focus({ preventScroll: true });
  }

  lockScroll() {
    document.body.classList.add('overflow-hidden');
  }

  unlockScroll() {
    document.body.classList.remove('overflow-hidden');
  }

  /* ----------------------------------------------------------- form state */

  serializeForm() {
    if (!this.form) return null;
    return new URLSearchParams(new FormData(this.form)).toString();
  }

  restoreForm(serialized) {
    if (!this.form) return;
    const params = new URLSearchParams(serialized);

    this.form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = params.getAll(input.name).includes(input.value);
    });

    this.form.querySelectorAll('[data-price-min], [data-price-max]').forEach((input) => {
      input.value = params.get(input.name) || '';
    });

    this.syncAllPriceSliders();
  }

  /* -------------------------------------------------------------- applying */

  /** Params that are ours to rewrite; everything else (q, type, sort_by…) is kept. */
  static isFilterParam(name) {
    return name.startsWith('filter.') || name.startsWith('filter_');
  }

  currentParams() {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    return params;
  }

  applyFilters() {
    const params = this.currentParams();

    Array.from(params.keys())
      .filter(FacetsPills.isFilterParam)
      .forEach((key) => params.delete(key));

    if (this.form) {
      new FormData(this.form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
    }

    this.snapshot = this.serializeForm();
    this.closeDrawer({ keepState: true });
    this.render(params);
  }

  clearFilters() {
    if (this.form) {
      this.form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.checked = false;
      });
      this.form.querySelectorAll('[data-price-min], [data-price-max]').forEach((input) => {
        input.value = '';
      });
      this.syncAllPriceSliders();
    }

    const params = this.currentParams();
    Array.from(params.keys())
      .filter(FacetsPills.isFilterParam)
      .forEach((key) => params.delete(key));

    this.snapshot = this.serializeForm();
    this.closeDrawer({ keepState: true });
    this.render(params);
  }

  applySort(value) {
    const params = this.currentParams();
    params.set('sort_by', value);

    const disclosure = this.querySelector('[data-sort-disclosure]');
    if (disclosure) disclosure.open = false;

    const current = this.querySelector('[data-sort-current]');
    const chosen = this.querySelector(`[data-sort-value="${CSS.escape(value)}"]`);
    if (current && chosen) current.textContent = chosen.textContent.trim();

    this.querySelectorAll('[data-sort-value]').forEach((option) => {
      option.setAttribute('aria-pressed', String(option.dataset.sortValue === value));
    });

    this.render(params);
  }

  applyUrl(href) {
    if (!href) return;
    const query = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
    const params = new URLSearchParams(query);
    params.delete('page');
    this.render(params);
  }

  render(params) {
    const grid = document.getElementById('ProductGridContainer')?.querySelector('.collection');
    if (grid) grid.classList.add('loading');

    if (typeof FacetFiltersForm === 'undefined') {
      window.location.search = params.toString();
      return;
    }

    FacetFiltersForm.renderPage(params.toString());
  }

  /* ------------------------------------------------------------ show more */

  /**
   * Value search inside a filter group (the Brand filter). Narrows the list to the
   * values whose label contains the term; the "show more" fold is suspended while a
   * term is active, since a match hidden behind it would look like a miss. Clearing
   * the field puts the fold back exactly as the button left it.
   */
  filterValues(input) {
    const body = input.closest('.facets-pills__group-body');
    if (!body) return;

    const term = input.value.trim().toLocaleLowerCase();
    const showMore = body.querySelector('[data-show-more]');
    const expanded = showMore?.getAttribute('aria-expanded') === 'true';
    let matches = 0;

    body.querySelectorAll('.facets-pills__value').forEach((item) => {
      const name = item.querySelector('.facets-pills__value-name')?.textContent.trim().toLocaleLowerCase() || '';
      if (term) {
        const hit = name.includes(term);
        item.hidden = !hit;
        if (hit) matches += 1;
      } else {
        item.hidden = item.classList.contains('facets-pills__value--extra') && !expanded;
        matches += 1;
      }
    });

    if (showMore) showMore.hidden = Boolean(term);

    const empty = body.querySelector('[data-filter-search-empty]');
    if (empty) empty.hidden = matches > 0;
  }

  toggleShowMore(button) {
    const body = button.closest('.facets-pills__group-body');
    if (!body) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    body.querySelectorAll('.facets-pills__value--extra').forEach((item) => {
      item.hidden = expanded;
    });

    button.setAttribute('aria-expanded', String(!expanded));
    const label = button.querySelector('[data-show-more-label]');
    if (label) {
      label.textContent = expanded ? button.dataset.labelMore : button.dataset.labelLess;
    }
  }

  /* ---------------------------------------------------------- price slider */

  syncAllPriceSliders() {
    this.querySelectorAll('price-range').forEach((range) => this.syncSliderFromInputs(range));
  }

  syncSliderFromInputs(range) {
    if (!range) return;

    const minInput = range.querySelector('[data-price-min]');
    const maxInput = range.querySelector('[data-price-max]');
    const minSlider = range.querySelector('[data-slider-min]');
    const maxSlider = range.querySelector('[data-slider-max]');
    if (!minSlider || !maxSlider) return;

    const rangeMax = Number(range.dataset.rangeMax) || Number(maxSlider.max) || 0;
    const min = FacetsPills.toNumber(minInput?.value, 0);
    const max = FacetsPills.toNumber(maxInput?.value, rangeMax);

    minSlider.value = Math.min(min, max);
    maxSlider.value = Math.max(min, max);

    this.paintTrack(range);
  }

  onSliderInput(slider) {
    const range = slider.closest('price-range');
    if (!range) return;

    const minSlider = range.querySelector('[data-slider-min]');
    const maxSlider = range.querySelector('[data-slider-max]');
    const minInput = range.querySelector('[data-price-min]');
    const maxInput = range.querySelector('[data-price-max]');

    let min = Number(minSlider.value);
    let max = Number(maxSlider.value);

    // Handles must not cross: the one being dragged pushes against the other.
    if (min > max) {
      if (slider === minSlider) {
        min = max;
        minSlider.value = String(min);
      } else {
        max = min;
        maxSlider.value = String(max);
      }
    }

    const rangeMax = Number(range.dataset.rangeMax) || Number(maxSlider.max) || 0;
    if (minInput) minInput.value = min === 0 ? '' : String(min);
    if (maxInput) maxInput.value = max >= rangeMax ? '' : String(max);

    this.paintTrack(range);
  }

  paintTrack(range) {
    const minSlider = range.querySelector('[data-slider-min]');
    const maxSlider = range.querySelector('[data-slider-max]');
    const fill = range.querySelector('.facets-pills__slider-fill');
    if (!minSlider || !maxSlider || !fill) return;

    const rangeMax = Number(maxSlider.max) || 1;
    const start = (Number(minSlider.value) / rangeMax) * 100;
    const end = (Number(maxSlider.value) / rangeMax) * 100;

    fill.style.insetInlineStart = `${start}%`;
    fill.style.width = `${Math.max(end - start, 0)}%`;
  }

  static toNumber(value, fallback) {
    if (value === undefined || value === null) return fallback;
    const parsed = parseFloat(String(value).replace(/[^\d.,-]/g, '').replace(',', '.'));
    return Number.isNaN(parsed) ? fallback : parsed;
  }
}

if (!customElements.get('facets-pills')) customElements.define('facets-pills', FacetsPills);

